/**
 * Generic state machine factory.
 * Creates a machine from a config object defining states, transitions, and actor rules.
 * Reusable across asset types (documents, controls, qualities).
 *
 * @param {Object} config - State machine configuration
 * @param {string} config.initial - Initial status
 * @param {string[]} config.reviewGroupOrder - Ordered array of reviewer group keys
 * @param {Object} config.states - State definitions with actions
 * @returns {Object} Machine instance with transition/query methods
 */
export function createMachine(config) {
  const { states, reviewGroupOrder } = config;

  function canTransition(fromStatus, action) {
    const state = states[fromStatus];
    if (!state) return false;
    return action in (state.actions || {});
  }

  function isCurrentActor(userSub, reviewerPipeline, currentActorIndex) {
    if (!reviewerPipeline || reviewerPipeline.length === 0) return false;
    if (currentActorIndex < 0 || currentActorIndex >= reviewerPipeline.length) return false;
    const current = reviewerPipeline[currentActorIndex];
    return userSub === (typeof current === 'object' ? current?.sub : current);
  }

  function buildPipeline(reviewers) {
    if (!reviewers) return [];
    return reviewGroupOrder.flatMap((group) => reviewers[group] || []);
  }

  function resolve(currentStatus, action, currentActorIndex, pipelineLength) {
    if (!canTransition(currentStatus, action)) return null;

    const actionConfig = states[currentStatus].actions[action];

    // Actions with next: null are handled externally (e.g., BE clone for newRevision)
    if (actionConfig.next === null) return null;

    // Reset index on reject/return/submit-from-returned
    if (actionConfig.resetIndex) {
      return { status: actionConfig.next, currentActorIndex: 0 };
    }

    // Dynamic resolution for approve: advance pipeline or go to APPROVED
    if (actionConfig.next === 'dynamic') {
      const nextIndex = currentActorIndex + 1;
      if (nextIndex >= pipelineLength) {
        return { status: 'APPROVED', currentActorIndex: currentActorIndex };
      }
      return { status: currentStatus, currentActorIndex: nextIndex };
    }

    // Static transition (e.g., submit -> REVIEWING, reject -> REJECTED)
    const newIndex = actionConfig.next === 'RETURNED' ? 0 : currentActorIndex;
    return { status: actionConfig.next, currentActorIndex: newIndex };
  }

  function getActions(status, userSub, reviewerPipeline, currentActorIndex, creatorSub, userRole) {
    const state = states[status];
    if (!state || !state.actions) return [];

    return Object.entries(state.actions)
      .filter(([, actionConfig]) => {
        switch (actionConfig.actorRule) {
          case 'creator':
            return userSub === creatorSub;
          case 'pipeline':
            return isCurrentActor(userSub, reviewerPipeline, currentActorIndex);
          case 'authorized':
            return true;
          case 'role':
            return userRole?.some((r) => actionConfig.requiredRole?.includes(r));
          default:
            return false;
        }
      })
      .map(([actionName]) => actionName);
  }

  return {
    canTransition,
    isCurrentActor,
    buildPipeline,
    resolve,
    getActions,
  };
}
